import {
  CalendarEvent,
  ICalendarEvent,
  ICalendarEventJS,
} from '@app/models/CalendarEvent'
import { ApplicationState } from '@app/store'
import { errorToast } from '@app/utils/notifications'
import { makeAuthenticatedRequest } from '@app/utils/request'
import { List, Record } from 'immutable'
import moment from 'moment'
import { Action, Reducer } from 'redux'
import { fork, put, select, takeLatest } from 'redux-saga/effects'
import { createSelector } from 'reselect'

// -------------------------
// STATE
// -------------------------
export enum AssignmentsCalendarScope {
  All = 'All',
  Mine = 'Mine',
}

export interface IAssignmentsCalendarState {
  readonly downloadUrl: string
  readonly endTime: number
  readonly error?: string
  readonly events: List<ICalendarEvent>
  readonly isLoading: boolean
  readonly scope: AssignmentsCalendarScope
  readonly startTime: number
}

const AssignmentsCalendarStateRecord = Record({
  downloadUrl: '',
  endTime: 0,
  error: undefined,
  events: List<CalendarEvent>(),
  isLoading: false,
  scope: AssignmentsCalendarScope.Mine,
  startTime: 0,
})

export class AssignmentsCalendarState extends AssignmentsCalendarStateRecord
  implements IAssignmentsCalendarState {
  public static fromJS(initialState?: Partial<IAssignmentsCalendarState>) {
    if (!initialState) {
      return new AssignmentsCalendarState()
    }

    const { events, ...rest } = initialState

    return new AssignmentsCalendarState({
      events: events
        ? List(events.map((e) => new CalendarEvent(e)))
        : List<CalendarEvent>(),
      ...rest,
    })
  }

  public readonly downloadUrl: string
  public readonly endTime: number
  public readonly error?: string
  public readonly events: List<CalendarEvent>
  public readonly isLoading: boolean
  public readonly scope: AssignmentsCalendarScope
  public readonly startTime: number

  constructor(props?: Partial<AssignmentsCalendarState>) {
    props ? super(props) : super()
  }
}

// -------------------------
// ACTIONS
// -------------------------

export enum AssignmentsCalendarActionTypes {
  LoadEvents = 'ASSIGNMENTS_CALENDAR::LOAD_EVENTS',
  LoadEventsFailure = 'ASSIGNMENTS_CALENDAR::LOAD_EVENTS_FAILURE',
  LoadEventsRequest = 'ASSIGNMENTS_CALENDAR::LOAD_EVENTS_REQUEST',
  LoadEventsSuccess = 'ASSIGNMENTS_CALENDAR::LOAD_EVENTS_SUCCESS',
  SetScope = 'ASSIGNMENTS_CALENDAR::SET_SCOPE',
}

interface ILoadEventsAction {
  end: number
  start: number
  type: AssignmentsCalendarActionTypes.LoadEvents
}

interface ILoadEventsFailureAction {
  error: string
  type: AssignmentsCalendarActionTypes.LoadEventsFailure
}

interface ILoadEventsRequestAction {
  end: number
  start: number
  type: AssignmentsCalendarActionTypes.LoadEventsRequest
}

interface ILoadEventsSuccessAction {
  downloadUrl: string
  events: CalendarEvent[]
  type: AssignmentsCalendarActionTypes.LoadEventsSuccess
}

interface ISetScopeAction {
  scope: AssignmentsCalendarScope
  type: AssignmentsCalendarActionTypes.SetScope
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
type KnownAction =
  | ILoadEventsFailureAction
  | ILoadEventsRequestAction
  | ILoadEventsSuccessAction
  | ISetScopeAction

// -------------------------
// ACTION CREATORS
// -------------------------

function loadEvents(start: number, end: number): ILoadEventsAction {
  return {
    end,
    start,
    type: AssignmentsCalendarActionTypes.LoadEvents,
  }
}

function loadEventsFailure(error: string): ILoadEventsFailureAction {
  return {
    error,
    type: AssignmentsCalendarActionTypes.LoadEventsFailure,
  }
}

function loadEventsRequest(
  start: number,
  end: number,
): ILoadEventsRequestAction {
  return {
    end,
    start,
    type: AssignmentsCalendarActionTypes.LoadEventsRequest,
  }
}

function loadEventsSuccess(
  events: CalendarEvent[],
  downloadUrl: string,
): ILoadEventsSuccessAction {
  return {
    downloadUrl,
    events,
    type: AssignmentsCalendarActionTypes.LoadEventsSuccess,
  }
}

function setScope(scope: AssignmentsCalendarScope): ISetScopeAction {
  return {
    scope,
    type: AssignmentsCalendarActionTypes.SetScope,
  }
}

export const actionCreators = {
  loadEvents,
  setScope,
}

// -------------------------
// REDUCER
// -------------------------
const initState = new AssignmentsCalendarState()

export const reducer: Reducer<AssignmentsCalendarState> = (
  state: AssignmentsCalendarState = initState,
  incomingAction: Action,
) => {
  const action = incomingAction as KnownAction
  switch (action.type) {
    case AssignmentsCalendarActionTypes.LoadEventsFailure:
      return state
        .set('error', undefined)
        .set('isLoading', false) as AssignmentsCalendarState

    case AssignmentsCalendarActionTypes.LoadEventsRequest:
      return state
        .set('startTime', action.start)
        .set('endTime', action.end)
        .set('isLoading', true) as AssignmentsCalendarState

    case AssignmentsCalendarActionTypes.LoadEventsSuccess:
      return state
        .set('downloadUrl', action.downloadUrl)
        .set('events', List(action.events))
        .set('isLoading', false) as AssignmentsCalendarState

    case AssignmentsCalendarActionTypes.SetScope:
      return state.set('scope', action.scope) as AssignmentsCalendarState

    default:
      // The following line guarantees that every action in the KnownAction union has been covered by a case above
      const exhaustiveCheck: never = action
  }

  return state
}

// -------------------------
// SAGAS
// -------------------------
function* loadEventsSaga(action: ILoadEventsAction) {
  try {
    yield put(loadEventsRequest(action.start, action.end))

    const scope: AssignmentsCalendarScope = yield select(selectScope())

    const query = [
      `start=${encodeURIComponent(moment.unix(action.start).toISOString())}`,
      `end=${encodeURIComponent(moment.unix(action.end).toISOString())}`,
    ]

    const baseUrl =
      scope === AssignmentsCalendarScope.All
        ? '/api/Calendars/Assignments'
        : '/api/Calendars/Assignments/My'

    const result = yield makeAuthenticatedRequest(
      `${baseUrl}?${query.join('&')}`,
    )

    if (result.success === true) {
      const events = result.results.map((c: ICalendarEventJS) =>
        CalendarEvent.fromJS(c),
      )
      yield put(loadEventsSuccess(events, result.downloadUrl))
    } else {
      yield put(loadEventsFailure(result.message))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(loadEventsFailure(err.message))
    yield put(errorToast('Oops!', err.message))
  }
}

function* watchLoadEvents() {
  yield takeLatest(AssignmentsCalendarActionTypes.LoadEvents, loadEventsSaga)
}

export function* assignmentsCalendarRootSaga() {
  yield fork(watchLoadEvents)
}

// -------------------------
// SELECTORS
// -------------------------
const selectModuleState = () => (state: ApplicationState) =>
  state.assignmentsCalendar

export const selectDownloadUrl = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.downloadUrl,
  )

export const selectEndTime = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.endTime,
  )

export const selectError = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.error,
  )

export const selectEvents = () =>
  createSelector(
    selectModuleState(),
    (mState) => {
      return mState.events
    },
  )

export const selectIsLoading = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.isLoading,
  )

export const selectScope = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.scope,
  )

export const selectStartTime = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.startTime,
  )
